  import { CartAddEvent } from '@theme/events';

  const escapeHtml = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[char]);

  function sanitizeRichHtml(value) {
    const doc = new DOMParser().parseFromString(String(value || ''), 'text/html');
    doc.querySelectorAll('script, style, iframe, object, embed, form, meta, link').forEach((node) => node.remove());
    doc.body.querySelectorAll('*').forEach((node) => {
      Array.from(node.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        const unsafeUrl = /^(href|src|xlink:href|formaction)$/.test(name)
          && /^\s*(javascript|data:text\/html):/i.test(attr.value);
        if (name.startsWith('on') || name === 'srcdoc' || unsafeUrl) node.removeAttribute(attr.name);
      });
    });
    return doc.body.innerHTML;
  }

  class MagonProductModal {
    constructor(dialog) {
      this.modal = dialog;
      this.modalVideoContainer = dialog.querySelector('.shoppable-modal-video');
      this.blocks = [];
      this.currentIndex = 0;
      this.startProductIndex = 0;
      this.isMuted = true;
      this.isPlaying = true;
      this._touchStartX = 0;
      this.pixDiscount = 0;
      this.showPix = true;
      this.showOldPrice = true;
      this.showInstallments = true;
      this.installments = 10;

      this.initEvents();

      document.addEventListener('magon:open-product-modal', (e) => {
        const d = e.detail || {};
        this.blocks = d.blocks || [];
        this.currentIndex = d.startIndex || 0;
        this.startProductIndex = d.startProductIndex || 0;
        this.pixDiscount = d.pixDiscount ?? 0;
        this.showPix = d.showPix ?? true;
        this.showOldPrice = d.showOldPrice ?? true;
        this.showInstallments = d.showInstallments ?? true;
        this.installments = d.installments ?? 10;
        this.isMuted = d.startMuted ?? true;
        this.isPlaying = true;
        this.renderModal();
        this.modal.showModal();
        document.body.style.overflow = 'hidden';
        // Pausa vídeos da grid de reels enquanto modal está aberto (poupa GPU/bateria)
        document.dispatchEvent(new CustomEvent('magon:reels-pause-all'));
      });
    }

    initEvents() {
      this.modal.querySelector('.shoppable-modal-close')?.addEventListener('click', () => this.closeModal());

      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal || e.target.classList.contains('shoppable-modal-content')) {
          this.closeModal();
        }
      });

      this.modal.querySelector('.modal-nav--prev')?.addEventListener('click', () => this.navigateModal(-1));
      this.modal.querySelector('.modal-nav--next')?.addEventListener('click', () => this.navigateModal(1));

      this._onKeyDown = (e) => {
        if (!this.modal?.open) return;
        if (e.key === 'ArrowLeft') this.navigateModal(-1);
        if (e.key === 'ArrowRight') this.navigateModal(1);
        if (e.key === 'Escape') this.closeModal();
      };
      document.addEventListener('keydown', this._onKeyDown);

      this.modalVideoContainer?.addEventListener('touchstart', (e) => {
        this._touchStartX = e.changedTouches[0].clientX;
      }, { passive: true });
      this.modalVideoContainer?.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - this._touchStartX;
        if (Math.abs(dx) > 50) this.navigateModal(dx < 0 ? 1 : -1);
      }, { passive: true });

      this.modal.querySelector('.modal-btn-play')?.addEventListener('click', () => this.togglePlay());
      this.modal.querySelector('.modal-btn-mute')?.addEventListener('click', () => this.toggleMute());
      this.modal.querySelector('.modal-btn-fullscreen')?.addEventListener('click', () => {
        const video = this.modalVideoContainer.querySelector('video');
        if (!video) return;
        // iOS Safari só permite fullscreen no elemento <video> (webkitEnterFullscreen)
        if (video.requestFullscreen) video.requestFullscreen();
        else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
        else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen();
      });

      // ATC e "ir ao carrinho" são por produto — delegação na lista, já que os
      // cards de produto são criados dinamicamente a cada renderModal().
      this.modal.querySelector('.modal-products-list')?.addEventListener('click', (e) => {
        const atcBtn = e.target.closest('.modal-product__atc');
        if (atcBtn) { this.addToCart(atcBtn.closest('.modal-product')); return; }
        const cartLink = e.target.closest('[data-open-cart-drawer]');
        if (cartLink) {
          this.closeModal();
          const drawer = document.querySelector('cart-drawer-component');
          if (drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
        }
      });

      this.modal.querySelector('.modal-look-bar__btn')?.addEventListener('click', () => this.addLook());
    }

    closeModal() {
      this.modal.close();
      this.modalVideoContainer.innerHTML = '';
      const list = this.modal.querySelector('.modal-products-list');
      if (list) list.innerHTML = '';
      document.body.style.overflow = '';
      // Retoma vídeos da grid de reels
      document.dispatchEvent(new CustomEvent('magon:reels-resume'));
    }

    navigateModal(direction) {
      const newIndex = this.currentIndex + direction;
      if (newIndex < 0 || newIndex >= this.blocks.length) return;
      this.currentIndex = newIndex;
      this.isPlaying = true;
      this.renderModal();
    }

    // Normaliza para { videoHtml, products: [...] } — aceita o formato novo
    // (reels: products[]) e o antigo (PDP: 1 produto no próprio bloco).
    normalizeBlock(raw) {
      if (raw && Array.isArray(raw.products)) {
        return { videoHtml: raw.videoHtml, products: raw.products };
      }
      return { videoHtml: raw && raw.videoHtml, products: (raw && raw.handle) ? [raw] : [] };
    }

    cartIsDrawer() {
      return (this.modal.dataset.cartType || '') === 'drawer';
    }

    renderModal() {
      const block = this.normalizeBlock(this.blocks[this.currentIndex]);
      if (!block) return;
      const products = block.products || [];

      // Setas de navegação entre vídeos (blocos)
      const hasNav = this.blocks.length > 1;
      const prevBtn = this.modal.querySelector('.modal-nav--prev');
      const nextBtn = this.modal.querySelector('.modal-nav--next');
      if (prevBtn) prevBtn.style.display = hasNav ? '' : 'none';
      if (nextBtn) nextBtn.style.display = hasNav ? '' : 'none';

      // Painel esquerdo: vídeo OU imagem do produto (quando não há vídeo — ex: hotspots)
      const leftPanel = this.modal.querySelector('.shoppable-modal-left');
      const controls = this.modal.querySelector('.modal-video-controls');
      const focusProduct = products[this.startProductIndex] || products[0];
      if (block.videoHtml) {
        this.modalVideoContainer.innerHTML = sanitizeRichHtml(block.videoHtml);
        const video = this.modalVideoContainer.querySelector('video');
        if (video) {
          video.muted = this.isMuted;
          video.autoplay = true; video.loop = true; video.playsInline = true;
          video.play().catch(() => {});
        }
        if (controls) controls.style.display = '';
        this.updatePlayIcon();
        this.updateMuteIcon();
        if (leftPanel) leftPanel.style.display = '';
      } else if (focusProduct && focusProduct.image) {
        // Sem vídeo → mostra a imagem do produto em foco
        this.modalVideoContainer.innerHTML = '<img class="shoppable-modal-img" src="' + escapeHtml(focusProduct.image) + '" alt="' + escapeHtml(focusProduct.title) + '">';
        if (controls) controls.style.display = 'none';
        if (leftPanel) leftPanel.style.display = '';
      } else if (leftPanel) {
        leftPanel.style.display = 'none';
      }

      // Painel de produtos (1 ou vários)
      const infoPanel = this.modal.querySelector('.shoppable-modal-info');
      const list = this.modal.querySelector('.modal-products-list');
      if (infoPanel) infoPanel.style.display = products.length ? '' : 'none';
      if (!list) return;
      list.innerHTML = '';
      list.classList.toggle('modal-products-list--multi', products.length > 1);

      products.forEach(p => {
        const el = this.createProductElement(p, products.length > 1);
        list.appendChild(el);
        if (p.handle) this.fetchAndRenderProduct(p.handle, el);
      });
      this.updateLookTotal();

      // Rola até o produto clicado no overlay (quando há vários)
      const startIdx = this.startProductIndex || 0;
      if (products.length > 1 && startIdx > 0 && list.children[startIdx]) {
        list.children[startIdx].scrollIntoView({ block: 'nearest' });
      }
      this.startProductIndex = 0;
    }

    createProductElement(p, isMulti) {
      const el = document.createElement('div');
      el.className = 'modal-product';
      el.dataset.handle = String(p.handle || '');
      el.innerHTML = `
        <img class="modal-product__thumb" src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}" loading="lazy">
        <div class="modal-product__body">
          <a class="modal-product__title" href="${escapeHtml(p.url)}" target="_blank" rel="noopener">${escapeHtml(p.title)}</a>
          <div class="modal-product__price">
            <span class="modal-product__pix"></span>
            <span class="modal-product__pix-label">pix</span>
            <span class="modal-product__installments"></span>
          </div>
          <div class="modal-product__variants"></div>
          <div class="modal-product__actions">
            <button class="modal-product__atc" type="button" disabled>Carregando...</button>
            <a class="modal-product__link" href="${escapeHtml(p.url)}" target="_blank" rel="noopener">Ver produto →</a>
          </div>
          <div class="modal-product__notification hidden">
            Produto adicionado!
            ${this.cartIsDrawer()
              ? '<button type="button" class="modal-cart-link" data-open-cart-drawer>Ir ao carrinho →</button>'
              : '<a class="modal-cart-link" href="/cart">Ir ao carrinho →</a>'}
          </div>
          ${isMulti ? '' : `<div class="modal-product__desc">${sanitizeRichHtml(p.desc)}</div>`}
        </div>`;
      this.updatePriceDisplay(el, p.price || 0, p.compare_at_price || 0);
      return el;
    }

    async fetchAndRenderProduct(handle, el) {
      const atcBtn = el.querySelector('.modal-product__atc');
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) {
          if (atcBtn) { atcBtn.textContent = 'Ver produto'; atcBtn.disabled = false; }
          return;
        }
        const product = await res.json();
        el._product = product;

        // Preenche a descrição (modo single) a partir do produto buscado — útil
        // quando o bloco não envia 'desc' (ex: hotspots).
        const descEl = el.querySelector('.modal-product__desc');
        if (descEl && product.description) descEl.innerHTML = sanitizeRichHtml(product.description);

        const firstAvailable = product.variants.find(v => v.available) || product.variants[0];
        el._variantId = firstAvailable?.id;

        if (firstAvailable) {
          this.updatePriceDisplay(el, firstAvailable.price, firstAvailable.compare_at_price || 0);
          this.updateLookTotal();
        }

        this.renderVariants(product, el);

        const isAvailable = firstAvailable?.available ?? false;
        if (atcBtn) {
          atcBtn.textContent = isAvailable ? 'Adicionar ao Carrinho' : 'Indisponível';
          atcBtn.disabled = !isAvailable;
        }

      } catch (e) {
        console.error('Magon Modal: Failed to fetch product', e);
        if (atcBtn) { atcBtn.textContent = 'Adicionar ao Carrinho'; atcBtn.disabled = false; }
      }
    }


    renderVariants(product, el) {
      const container = el.querySelector('.modal-product__variants');
      if (!container) return;
      container.innerHTML = '';

      if (!product.options || product.options.length === 0) return;

      const optionNames = product.options.map(o => (typeof o === 'object' && o !== null) ? (o.name || String(o)) : String(o));
      if (optionNames.length === 1 && optionNames[0] === 'Title') return;

      optionNames.forEach((optionName, optionIndex) => {
        const values = [...new Set(product.variants.map(v => v[`option${optionIndex + 1}`]))];

        const group = document.createElement('div');
        group.className = 'modal-variant-group';

        const label = document.createElement('div');
        label.className = 'modal-variant-label';
        label.textContent = optionName;
        group.appendChild(label);

        const optionsRow = document.createElement('div');
        optionsRow.className = 'modal-variant-options';

        values.forEach(value => {
          const btn = document.createElement('button');
          btn.className = 'modal-variant-btn';
          btn.type = 'button';
          btn.textContent = value;
          btn.dataset.option = String(optionIndex + 1);
          btn.dataset.value = value;

          const hasAvailable = product.variants.some(v =>
            v[`option${optionIndex + 1}`] === value && v.available
          );
          if (!hasAvailable) btn.disabled = true;

          const defaultVariant = product.variants.find(v => v.id === el._variantId);
          if (defaultVariant && defaultVariant[`option${optionIndex + 1}`] === value) {
            btn.classList.add('active');
          }

          btn.addEventListener('click', () => this.selectOption(el, optionIndex + 1, value, btn, optionsRow));
          optionsRow.appendChild(btn);
        });

        group.appendChild(optionsRow);
        container.appendChild(group);
      });
    }

    selectOption(el, optionNumber, value, clickedBtn, optionsRow) {
      optionsRow.querySelectorAll('.modal-variant-btn').forEach(b => b.classList.remove('active'));
      clickedBtn.classList.add('active');

      const selections = {};
      el.querySelectorAll('.modal-variant-group').forEach((group, idx) => {
        const activeBtn = group.querySelector('.modal-variant-btn.active');
        if (activeBtn) selections[idx + 1] = activeBtn.dataset.value;
      });

      const matched = el._product?.variants.find(v =>
        Object.entries(selections).every(([opt, val]) => v[`option${opt}`] === val)
      );

      if (matched) {
        el._variantId = matched.id;
        this.updatePriceDisplay(el, matched.price, matched.compare_at_price || 0);
        const atcBtn = el.querySelector('.modal-product__atc');
        if (atcBtn) {
          atcBtn.disabled = !matched.available;
          atcBtn.textContent = matched.available ? 'Adicionar ao Carrinho' : 'Indisponível';
        }
        this.updateLookTotal();
      }
    }

    updatePriceDisplay(el, priceCents) {
      const pixFactor = (100 - this.pixDiscount) / 100;
      const pixCents  = Math.round(priceCents * pixFactor);
      const shownCents = this.showPix ? pixCents : priceCents; // segue o toggle do card
      el._pixCents = shownCents;   // total do look (valor exibido: PIX ou cartão)
      el._priceCents = priceCents; // parcelamento do look (sempre sobre o cheio)

      const pixEl = el.querySelector('.modal-product__pix');
      if (pixEl) pixEl.textContent = this.formatMoney(shownCents);

      const pixLabel = el.querySelector('.modal-product__pix-label');
      if (pixLabel) pixLabel.style.display = this.showPix ? '' : 'none';

      const installEl = el.querySelector('.modal-product__installments');
      if (installEl) {
        if (this.showInstallments) {
          const perInstallment = Math.round(priceCents / this.installments);
          installEl.textContent = `${this.installments}x de ${this.formatMoney(perInstallment)}`;
          installEl.style.display = '';
        } else {
          installEl.style.display = 'none';
        }
      }
    }

    // Total do look (soma dos preços PIX exibidos) + visibilidade do rodapé.
    updateLookTotal() {
      const bar = this.modal.querySelector('.modal-look-bar');
      if (!bar) return;
      const items = Array.from(this.modal.querySelectorAll('.modal-product'));
      if (items.length < 2) { bar.classList.add('hidden'); return; }
      bar.classList.remove('hidden');

      let totalPix = 0, totalRegular = 0;
      items.forEach(el => {
        if (typeof el._pixCents === 'number') totalPix += el._pixCents;
        if (typeof el._priceCents === 'number') totalRegular += el._priceCents;
      });
      const totalEl = bar.querySelector('.modal-look-bar__total');
      if (totalEl) totalEl.textContent = this.formatMoney(totalPix);

      const totalPixLabel = bar.querySelector('.modal-look-bar__pix-label');
      if (totalPixLabel) totalPixLabel.style.display = this.showPix ? '' : 'none';

      const instEl = bar.querySelector('.modal-look-bar__installments');
      if (instEl) {
        if (this.showInstallments && totalRegular > 0) {
          const per = Math.round(totalRegular / this.installments);
          instEl.textContent = `${this.installments}x de ${this.formatMoney(per)}`;
          instEl.style.display = '';
        } else {
          instEl.style.display = 'none';
        }
      }
      const btn = bar.querySelector('.modal-look-bar__btn');
      if (btn) btn.textContent = `Adicionar look completo (${items.length} peças)`;
    }

    // Adiciona todos os produtos do look de uma vez (kit/combo).
    async addLook() {
      const bar = this.modal.querySelector('.modal-look-bar');
      const btn = bar?.querySelector('.modal-look-bar__btn');
      if (!btn) return;
      const items = Array.from(this.modal.querySelectorAll('.modal-product'))
        .map(el => el._variantId)
        .filter(Boolean)
        .map(id => ({ id, quantity: 1 }));
      if (!items.length) return;

      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adicionando...';
      try {
        const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
          .map(s => s.dataset.sectionId).filter(Boolean);
        const body = { items };
        if (sectionIds.length) body.sections = sectionIds.join(',');

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await res.json();

        if (res.ok) {
          document.dispatchEvent(new CartAddEvent({}, String(items[0].id), {
            source: 'magon-reels-look',
            itemCount: items.length,
            sections: data.sections,
          }));
          btn.textContent = 'Look adicionado!';
          setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 1500);
        } else {
          btn.textContent = (data.description || data.message || 'Erro').slice(0, 28);
          setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 2500);
        }
      } catch (e) {
        console.error('Magon Modal: add look failed', e);
        btn.textContent = original; btn.disabled = false;
      }
    }

    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, window.theme?.moneyFormat || 'R$ {{amount}}').replace(',00', '');
      }
      const v = (cents / 100).toFixed(2);
      const parts = v.split('.');
      const int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      // Omite os centavos quando forem ",00" (consistência com o resto do site)
      return parts[1] === '00' ? 'R$ ' + int : 'R$ ' + int + ',' + parts[1];
    }

    async addToCart(el) {
      if (!el) return;
      const btn = el.querySelector('.modal-product__atc');
      if (!btn) return;
      const variantId = el._variantId;
      // Sem variante carregada (fetch falhou): abre a página do produto.
      if (!variantId) {
        if (el.dataset.handle) window.open(`/products/${el.dataset.handle}`, '_blank');
        return;
      }
      const notification = el.querySelector('.modal-product__notification');

      btn.disabled = true;
      btn.textContent = 'Adicionando...';

      try {
        // Coleta IDs de cart-items-component para refresh do mini-cart/header
        const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
          .map(s => s.dataset.sectionId)
          .filter(Boolean);

        const body = { id: variantId, quantity: 1 };
        if (sectionIds.length) body.sections = sectionIds.join(',');

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        });
        const data = await res.json();

        if (res.ok) {
          if (notification) notification.classList.remove('hidden');
          // Dispara evento padrão da Horizon → drawer auto-open + counter atualiza
          document.dispatchEvent(new CartAddEvent({}, String(variantId), {
            source: 'magon-reels-modal',
            itemCount: 1,
            sections: data.sections,
          }));
          setTimeout(() => {
            if (notification) notification.classList.add('hidden');
          }, 6000);
          btn.textContent = 'Adicionado!';
          setTimeout(() => {
            btn.textContent = 'Adicionar ao Carrinho';
            btn.disabled = false;
          }, 1500);
        } else {
          console.error('Magon Modal: ATC error', data.description || data.message);
          btn.textContent = (data.description || data.message || 'Erro').slice(0, 32);
          setTimeout(() => {
            btn.textContent = 'Adicionar ao Carrinho';
            btn.disabled = false;
          }, 2500);
        }
      } catch (e) {
        console.error('Magon Modal: ATC failed', e);
        btn.textContent = 'Adicionar ao Carrinho';
        btn.disabled = false;
      }
    }

    togglePlay() {
      const video = this.modalVideoContainer?.querySelector('video');
      if (!video) return;
      if (video.paused) { video.play().catch(() => {}); this.isPlaying = true; }
      else { video.pause(); this.isPlaying = false; }
      this.updatePlayIcon();
    }

    toggleMute() {
      const video = this.modalVideoContainer?.querySelector('video');
      if (!video) return;
      this.isMuted = !this.isMuted;
      video.muted = this.isMuted;
      this.updateMuteIcon();
    }

    updatePlayIcon() {
      const playIcon  = this.modal?.querySelector('.modal-icon-play');
      const pauseIcon = this.modal?.querySelector('.modal-icon-pause');
      if (playIcon)  playIcon.style.display  = this.isPlaying ? 'none' : 'flex';
      if (pauseIcon) pauseIcon.style.display = this.isPlaying ? 'flex' : 'none';
    }

    updateMuteIcon() {
      const muteIcon   = this.modal?.querySelector('.modal-icon-mute');
      const unmuteIcon = this.modal?.querySelector('.modal-icon-unmute');
      if (muteIcon)   muteIcon.style.display   = this.isMuted ? 'flex' : 'none';
      if (unmuteIcon) unmuteIcon.style.display = this.isMuted ? 'none' : 'flex';
    }
  }

  const dialog = document.getElementById('magon-global-product-modal');
  if (dialog) window._magonProductModal = new MagonProductModal(dialog);
